import { Box, Fade, Flex, Text, keyframes } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useAudioRecorder } from 'react-audio-voice-recorder';
import LaunchdeckAudioPlayer from '../../../../global/components/helper-components/components/LaunchdeckAudioPlayer';
import LaunchdeckButton from '../../../../global/components/helper-components/components/LaunchdeckButton';
import { MdSettingsVoice } from "react-icons/md";
import { displayReactIcon } from '../../../helpers/dsr-display-helpers';
import ReactIconDisplay from '../../../../global/components/helper-components/components/ReactIconDisplay';
import { RiDeleteBinFill } from "react-icons/ri";
import { IoPauseOutline, IoPlay, IoStop } from "react-icons/io5";

const AudioRecorderModule = ({ handleUploadRecordedAudioFile, handleChangeToUploadMode, isFree }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [timerId, setTimerId] = useState(null);

  const {
    startRecording,
    stopRecording,
    togglePauseResume,
    recordingBlob,
    isRecording,
    isPaused,
    recordingTime,
    mediaRecorder,
  } = useAudioRecorder();

  // Use useEffect to watch for recordingBlob changes after recording stops
  useEffect(() => {
    if (recordingBlob) {
      addAudioElement(recordingBlob);
    }
  }, [recordingBlob]);


  const addAudioElement = (blob) => {
    // Create a URL from the Blob and set it in state
    const url = URL.createObjectURL(blob);
    setAudioUrl(url);

    // Cleanup the URL object when component unmounts or URL changes
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  };

  // Determine the maximum recording time based on the `isFree` flag
  const maxRecordingTime = isFree ? 60 : 600; // 60 seconds (1 minute) for free, 600 seconds (10 minutes) otherwise
  const [timeLeft, setTimeLeft] = useState(maxRecordingTime);


  useEffect(() => {
    if (isRecording) {
      // Update time left based on recording time
      setTimeLeft(maxRecordingTime - Math.floor(recordingTime));
    }
  }, [isRecording, recordingTime, maxRecordingTime]);

  useEffect(() => {
    if (isRecording && timeLeft < 0) {
      handleStopRecording()
    }
  }, [isRecording, timeLeft]);

  useEffect(() => {
    // Reset time left when the recording stops
    if (!isRecording && !isPaused) {
      setTimeLeft(maxRecordingTime);
    }
  }, [isRecording, maxRecordingTime, isPaused]);

  // Start recording and reset any previous recording state
  const handleStartRecording = () => {
    startRecording();
    setAudioUrl(null);
    setAudioBlob(null);
  };

  // Stop and finalize recording
  const handleStopRecording = () => {
    stopRecording();
    clearInterval(timerId);  // Clear the timer when recording stops
    setTimeLeft(0); // Reset time left after stopping

    if (recordingBlob) {
      const url = URL.createObjectURL(recordingBlob);

      setAudioBlob(recordingBlob);
      setAudioUrl(url);
    }
  };

  // Pause/Resume recording
  const handleTogglePauseResume = () => {
    togglePauseResume();
  };

  // Upload audio to server
  const uploadAudio = async () => {
    if (!recordingBlob) return;

    const file = new File([recordingBlob], 'Voice_Recording.webm', { type: 'audio/webm' });
    handleUploadRecordedAudioFile(file)
  };

  // User discards the file
  const resetState = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setTimeLeft(0)
    setTimerId(null)
  }
  const handleDiscardFile = () => {
    resetState()
  }

  const handleChangeToUpload = () => {
    handleDiscardFile()
    handleChangeToUploadMode()
  }

  const blinking = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
`;

  return (
    <Flex direction='column' w='100%' justify='center' align='center' fontSize="0.875em">

      {/* Preview the recording */}
      {audioUrl ? (
        <>
          <Box p='10px' my='1em' w='100%'>
            <Fade in={true} transition={{ enter: { duration: 0.5 }, exit: { duration: 0.5 } }}>
              <Flex justify='flex-end' w='100%'>
                <Flex gap={2} p='0.1em' justify='center' align='center' rounded='50%' color='gray.500' onClick={() => handleDiscardFile()}>
                  <ReactIconDisplay reactIcon={RiDeleteBinFill} fontSize='0.875em' mr='0em' />
                  <Box fontSize='0.75em'>Discard</Box>
                </Flex>
              </Flex>    
              <LaunchdeckAudioPlayer
                autoPlay={false}
                file={{ url: audioUrl, fileName: "Recording preview" }}
                showFileName={true}
              />
            </Fade>
          </Box>

          <LaunchdeckButton
            bg="brandDark.500"
            color="white"
            px="2em"
            // label="Save"
            onClick={() => uploadAudio(audioBlob)}
          >
            <Flex gap={4} align='center'>
              {displayReactIcon('save', '1.5em')} <Box fontSize='0.875em'>Save recording</Box>
            </Flex>
          </LaunchdeckButton>
        </>
      )
        :
        <>
          <Box
            fill="brandDark.500"
            as={MdSettingsVoice}
            width="48px"
            height="3.5em"
            my="2em"
          />

          {mediaRecorder && (
            <Flex w='100%' rounded='0.25em' justify='center' align='center'
              animation={!isPaused && `${blinking} 2s infinite`} // Apply blinking animation
              gap={2}
            >
              {isPaused ?
                <>
                  <Box className="static_grey_dot" />
                  <Text
                    fontSize="0.875em"
                    color="gray.500"
                  >
                    Paused
                  </Text>
                </>
                :
                <>
                  <Box className="blinking_green_dot" />
                  <Text
                    fontSize="0.875em"
                    color="gray.500"
                  >
                    Recording
                  </Text>
                </>
              }

            </Flex>
          )

          }

          {/* Start, Pause/Resume, and Stop Buttons */}
          {!isRecording &&
            <LaunchdeckButton
              bg="brandDark.500"
              color="white"
              px="2em"
              fontSize='0.8em'
              label="Record voice message"
              onClick={handleStartRecording}
            />
          }

        </>
      }

      {isRecording && (
        <Flex justify='center' align='center' gap={4} mt='1em'>
          <Box onClick={handleStopRecording}>
            <ReactIconDisplay
              reactIcon={IoStop} fontSize='1.1em' mr='0em'
            />

          </Box>

          <Box onClick={handleTogglePauseResume}>
            <ReactIconDisplay
              reactIcon={isPaused ? IoPlay : IoPauseOutline} fontSize='1.1em' mr='0em'
            />

          </Box>


        </Flex>
      )}

      {!audioUrl &&
        <>
          <Flex
            direction="column"
            my="1.5em"
            fontSize="0.875em"
            color="brandDark.500"
            justify="center"
            align="center"
          >

            <Box mt="0.8em" textAlign="center">
              <Box color="gray.500">{isRecording ? "Time remaining" : "Max time"}</Box>
              {isRecording ? `${timeLeft}s` : `${maxRecordingTime} seconds`}
            </Box>
          </Flex>

          {!isRecording &&
            <LaunchdeckButton
              bg="none"
              color="gray.500"
              label="Upload audio file >"
              fontSize='0.75em'
              border='none'
              onClick={handleChangeToUpload}
            />
          }
        </>
      }
    </Flex>
  );
};

export default AudioRecorderModule;
